import React, { FC, useState, Suspense } from 'react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import {
  Avatar,
  AvatarSize,
  Box,
  Button,
  FormControl,
  Heading,
  Intrinsic,
  SideSheetContentCol,
  Spinner,
  SpinnerSize,
  Text,
  TextArea,
  ButtonAppearance,
  ButtonSize,
  Icon,
} from '@bechtel/gds';
import {
  AddBlockAttachmentError,
  AddBlockAttachmentInput,
  AddBlockAttachmentPayload,
  Attachment,
  AttachmentMetadataInput,
  Block,
  BlockStatusCode,
  CommodityWrapper,
  DeleteBlockAttachmentError,
  DeleteBlockAttachmentInput,
  DeleteBlockAttachmentPayload,
} from '@bechtel/types';
import { Field, Formik, FormikHelpers as FormikActions } from 'formik';
import { get, map, orderBy, uniq } from 'lodash';
import { object, string } from 'yup';
import { ADD_BLOCK_COMMENT, GET_BLOCK_BY_ID, ADD_BLOCK_ATTACHMENT, DELETE_BLOCK_ATTACHMENT } from '@bechtel/client-gql';
import {
  useBlockPermission,
  ImageViewer,
  useImageViewerState,
  ErrorRetry,
  useName,
  FileUpload,
  FileUploadResponse,
  FileUploadInput,
  useAppUser,
  ErrorBoundary,
  SuspenseLoader,
  SetFile,
  CommentHistory,
} from '@bechtel/client-common';
import { BlockTypeMessages, BlockTypeDescriptionMessages } from '~/components/atoms';
import { AttachmentContainerStyled, BlockImgStyled, ListContainerStyled, ResolveSubHeadingStyled } from './styled';
import { t, Trans } from '@lingui/macro';
import { FormikBlockCommentState, SingleBlockProps, SubHeadingProps } from './types';
import { DeleteAttachmentStyled } from './styled';
import { ApolloError } from '@apollo/client';
import produce from 'immer';
import { AddRemoveBlockAttachmentError } from '~/components/molecules';

const blockCommentValidationSchema = object().shape({
  comments: string().max(2000, t`Please limit comments to 2000 characters').required('Comment is mandatory`),
});

const blockCommentInitialValues: FormikBlockCommentState = {
  comments: '',
};

export const SubHeading: FC<SubHeadingProps> = ({ heading }) => {
  return (
    <ResolveSubHeadingStyled>
      <Heading family="graphik" size="400" weight="600" color="grey.1000">
        {heading}
      </Heading>
    </ResolveSubHeadingStyled>
  );
};

export const SingleBlock: FC<SingleBlockProps> = ({
  assignedCrewMembers,
  weldCommodityAssignees,
  facilities,
  blockId,
}) => {
  const { user } = useAppUser();
  const { getFullName } = useName();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const [addRemoveBlockAttachmentError, setAddRemoveBlockAttachmentError] = useState<
    DeleteBlockAttachmentError | AddBlockAttachmentError | ApolloError | null
  >(null);
  const { canEditBlock, canResolveBlock } = useBlockPermission(
    assignedCrewMembers,
    facilities ? facilities : [],
    null,
    weldCommodityAssignees
  );
  const [addBlockComment, { loading: addCommentLoading }] = useMutation(ADD_BLOCK_COMMENT);
  const [addBlockAttachment, { loading: addBlockAttachmentLoading }] = useMutation<
    { addBlockAttachment: AddBlockAttachmentPayload },
    { input: AddBlockAttachmentInput }
  >(ADD_BLOCK_ATTACHMENT, {
    onCompleted: (data: { addBlockAttachment: AddBlockAttachmentPayload }) => {
      const addBlockAttachmentError = get(data, 'addBlockAttachment.error', null);
      if (!addBlockAttachmentError) return;
      setAddRemoveBlockAttachmentError(addBlockAttachmentError);
    },
    onError: (error) => {
      setAddRemoveBlockAttachmentError(error);
    },
  });
  const [deleteBlockAttachment, { loading: deleteBlockAttachmentLoading }] = useMutation<
    { deleteBlockAttachment: DeleteBlockAttachmentPayload },
    { input: DeleteBlockAttachmentInput }
  >(DELETE_BLOCK_ATTACHMENT, {
    onCompleted: (data: { deleteBlockAttachment: DeleteBlockAttachmentPayload }) => {
      const deleteBlockAttachmentError = get(data, 'deleteBlockAttachment.error', null);
      if (!deleteBlockAttachmentError) return;
      setAddRemoveBlockAttachmentError(deleteBlockAttachmentError);
    },
    onError: (error) => {
      setAddRemoveBlockAttachmentError(error);
    },
  });
  const { loading, error, data, refetch, networkStatus } = useQuery<{ blockById: Block }>(GET_BLOCK_BY_ID, {
    variables: {
      id: blockId,
    },
    notifyOnNetworkStatusChange: true,
  });
  let newSetFiles: SetFile | undefined = undefined;
  if (loading || networkStatus === NetworkStatus.refetch)
    return (
      <Box
        data-testid="loading"
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'center' }}
        p={{ sm: '500' }}
        style={{ flexGrow: 1 }}
      >
        <Spinner size={SpinnerSize.LARGE} />
      </Box>
    );
  if (!loading && error)
    return (
      <ErrorRetry refetch={refetch}>
        <Trans>There was a problem loading Block Detail. Please click the button below to try again.</Trans>
      </ErrorRetry>
    );
  if (!loading && !error && (!data || !data.blockById))
    return (
      <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
        <Trans> No block detail found.</Trans>
      </Box>
    );
  const selectedBlock = data && data.blockById;
  const isUserBlockCreator = user?.crewMember.id === selectedBlock?.creator.id;
  const isBlockResolved = selectedBlock && selectedBlock.currentStatusI18n === BlockStatusCode.BLOCK_RESOLVED;
  const orderedComments = selectedBlock ? orderBy(selectedBlock.comments, ['createdAt'], ['desc']) : [];
  // resolution notes entered while resolving block
  const resolutionNotes = isBlockResolved && orderedComments.length > 0 ? orderedComments.shift().comment : '';
  const impactedCommodities =
    selectedBlock && selectedBlock.impactedCommodities && selectedBlock.impactedCommodities.length > 0
      ? uniq(
          map(selectedBlock.impactedCommodities, (commodity: CommodityWrapper) => {
            const commodityData = commodity.weld || commodity.materialCommodity;
            return commodityData && commodityData.tag;
          })
        )
      : [];
  const impactedMilestones =
    selectedBlock && selectedBlock.impactedMilestones && selectedBlock.impactedMilestones.length > 0
      ? uniq(map(selectedBlock.impactedMilestones, 'name'))
      : [];
  const images =
    selectedBlock && selectedBlock.attachment && selectedBlock.attachment.length > 0
      ? map(selectedBlock.attachment, 'url')
      : [];
  return (
    <SideSheetContentCol>
      {visibleImageViewer && (
        <ImageViewer
          images={images}
          selectedImageIndex={selectedImageIndex}
          visible={visibleImageViewer}
          closable={true}
          onDismiss={closeImageViewer}
        />
      )}
      <SubHeading heading={t`Block Details`} />
      <Box data-testid="block-detail" p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '300' }}>
            <Text size="400" color="grey.1000">
              <Trans> Block Type:</Trans>{' '}
              {selectedBlock && selectedBlock.type && selectedBlock.type.title && (
                <Trans id={BlockTypeMessages[selectedBlock.type.title].id}></Trans>
              )}
            </Text>
          </Box>
          <Text size="200" color="grey.1000">
            {selectedBlock && selectedBlock.type && selectedBlock.type.description && (
              <Trans id={BlockTypeDescriptionMessages[selectedBlock.type.description].id}></Trans>
            )}
          </Text>
        </Box>
        <Box mb={{ sm: '500' }} alignItems={{ sm: 'center' }}>
          <Avatar
            size={AvatarSize.LARGE}
            src={
              selectedBlock && selectedBlock.creator && selectedBlock.creator.imageUrl
                ? selectedBlock.creator.imageUrl
                : undefined
            }
          />
          <Box flexDirection={{ sm: 'column' }} ml={{ sm: '200' }}>
            <Text family="graphik" size="400" weight={600} color="grey.1000">
              <Trans> Set By:</Trans>
            </Text>
            <Box mt={{ sm: '200' }}>
              <Text size="300" color="grey.600">
                {selectedBlock &&
                  selectedBlock.creator &&
                  getFullName(selectedBlock.creator.firstName, selectedBlock.creator.lastName)}
              </Text>
            </Box>
          </Box>
        </Box>
        <Box mb={{ sm: '500' }}>
          <Text family="graphik" color="grey.1000" weight={600} size="500">
            <Trans>Details</Trans>
          </Text>
        </Box>
        <Box mb={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '300' }}>
            <Text size="300" family="graphik" color="grey.1000" weight={600}>
              <Trans>Impacted Commodities:</Trans>
            </Text>
          </Box>
          {impactedCommodities && impactedCommodities.length > 0 && (
            <ListContainerStyled>
              {map(impactedCommodities, (commodityTag: string) => {
                return (
                  <Text
                    data-testid={`impacted-commodity-${commodityTag}`}
                    key={commodityTag}
                    size="300"
                    color="grey.1000"
                  >
                    {commodityTag}
                  </Text>
                );
              })}
            </ListContainerStyled>
          )}
        </Box>

        <Box flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '300' }}>
            <Text size="300" family="graphik" color="grey.1000" weight={600}>
              <Trans>Impacted Milestones:</Trans>
            </Text>
          </Box>
          {impactedMilestones && impactedMilestones.length > 0 && (
            <ListContainerStyled>
              {map(impactedMilestones, (milestoneName: string) => {
                return (
                  <Text
                    data-testid={`impacted-milestone-${milestoneName}`}
                    key={milestoneName}
                    size="300"
                    color="grey.1000"
                  >
                    {milestoneName}
                  </Text>
                );
              })}
            </ListContainerStyled>
          )}
        </Box>
        {selectedBlock && selectedBlock.attachment && selectedBlock.attachment.length > 0 && (
          <AttachmentContainerStyled>
            {map(selectedBlock.attachment, (attachmentItem: Attachment, index: number) => {
              return (
                <Box
                  style={{ position: 'relative' }}
                  key={attachmentItem.id}
                  onClick={() => {
                    setSelectedImageIndex(index);
                    openImageViewer();
                  }}
                >
                  <Intrinsic ratio={{ sm: '16:9' }}>
                    <BlockImgStyled src={attachmentItem.url} />
                  </Intrinsic>
                  {isUserBlockCreator && (
                    <DeleteAttachmentStyled>
                      <Button
                        appearance={ButtonAppearance.SECONDARY}
                        size={ButtonSize.SMALL}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const { data: deleteBlockAttachmentData } = await deleteBlockAttachment({
                            variables: {
                              input: { attachmentId: attachmentItem.id },
                            },
                          });
                          if (
                            newSetFiles &&
                            deleteBlockAttachmentData &&
                            !deleteBlockAttachmentData.deleteBlockAttachment.error
                          ) {
                            newSetFiles(
                              produce((draft) => {
                                //@ts-ignore splice will exist on draft since it is an array
                                draft.splice(index, 1);
                              })
                            );
                          }
                        }}
                      >
                        {deleteBlockAttachmentLoading && <Spinner />}
                        {!deleteBlockAttachmentLoading && (
                          <>
                            <Box mr={{ sm: '300' }}>
                              <Icon icon="trash-alt" fillColor="red.800" />
                            </Box>
                            <Trans>Delete</Trans>
                          </>
                        )}
                      </Button>
                    </DeleteAttachmentStyled>
                  )}
                </Box>
              );
            })}
          </AttachmentContainerStyled>
        )}
        {isUserBlockCreator && (
          <Box mt={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
            <FormControl
              label={t`Upload a Photo (${selectedBlock?.attachment.length ? selectedBlock?.attachment.length : 0}/3)`}
            >
              <FileUpload
                maxFiles={3}
                maxFileSizeMB={10}
                filesCount={selectedBlock?.attachment.length ? selectedBlock?.attachment.length : 0}
                acceptFileTypes="image/jpeg, image/png"
                onSuccess={(res: FileUploadResponse, file) => {
                  const newMediaData: AttachmentMetadataInput = {
                    fileName: file.name,
                    fileSize: file.size,
                    attachmentId: res.attachmentId,
                    mimeType: file.type,
                  };
                  addBlockAttachment({
                    variables: { input: { id: selectedBlock?.id, attachmentMetadata: [newMediaData] } },
                  });
                }}
              >
                {({ getInputProps, getRootProps, loading, setFiles, acceptFileTypes }) => {
                  newSetFiles = setFiles;
                  return (
                    <FileUploadInput
                      getInputProps={getInputProps}
                      getRootProps={getRootProps}
                      loading={loading || addBlockAttachmentLoading}
                      acceptFileTypes={acceptFileTypes}
                    />
                  );
                }}
              </FileUpload>
            </FormControl>
          </Box>
        )}
      </Box>
      <SubHeading heading={t`Comments`} />
      {selectedBlock && orderedComments.length > 0 && (
        <CommentHistory
          canEditBlock={canEditBlock}
          blockStatus={selectedBlock.currentStatusI18n}
          blockComments={orderedComments}
        />
      )}
      {canEditBlock && !isBlockResolved && (
        <Formik
          initialValues={blockCommentInitialValues}
          validationSchema={blockCommentValidationSchema}
          onSubmit={(values: FormikBlockCommentState, actions: FormikActions<FormikBlockCommentState>) => {
            const addBlockCommentInput = {
              blockId: selectedBlock?.id,
              comment: values.comments,
            };
            addBlockComment({ variables: { input: addBlockCommentInput } });
            actions.resetForm({ values: blockCommentInitialValues });
            actions.setSubmitting(false);
          }}
        >
          {(subFormikProps) => {
            return (
              <Box mt={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
                <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                  <Field name="comments">
                    {({ field }) => (
                      <FormControl label={<Trans>Comment</Trans>}>
                        <TextArea maxLength={2000} rows={5} {...field} />
                      </FormControl>
                    )}
                  </Field>

                  <Box mt={{ sm: '500' }}>
                    <Button disabled={!subFormikProps.values.comments} onClick={() => subFormikProps.handleSubmit()}>
                      {addCommentLoading && <Spinner />}
                      {!addCommentLoading && <Trans>Save Comment</Trans>}
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          }}
        </Formik>
      )}
      <SubHeading heading={t`Resolution Notes`} />
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text size={400} color="grey.1000" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
          {resolutionNotes}
        </Text>
        {!isBlockResolved && canResolveBlock && (
          <Field name="resolution">
            {({ field }) => (
              <FormControl label={<Trans>Resolution (Required)</Trans>}>
                <TextArea maxLength={2000} rows={5} {...field} />
              </FormControl>
            )}
          </Field>
        )}
      </Box>
      {addRemoveBlockAttachmentError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <AddRemoveBlockAttachmentError
              addRemoveBlockAttachmentError={addRemoveBlockAttachmentError}
              close={() => {
                setAddRemoveBlockAttachmentError(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </SideSheetContentCol>
  );
};
